import unionBy from 'lodash/unionBy';

export const AI_ENGINES = [
  { id: 'poll', label: 'Poll' },
  { id: 'quiz', label: 'Quiz' },
  { id: 'personality', label: 'Personality' },
  { id: 'story', label: 'Story' },
];

export const labelOfEngine = id => AI_ENGINES.find(engine => engine.id === id)?.label || id;

export const nonNegativeNumber = value => {
  if (value === '' || value === undefined || value === null) {
    return undefined;
  }
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? undefined : 'Enter 0 or a positive amount';
};

// Currently-selected engines as MultiselectField rows (label + id + isSelected).
export const selectedEngineRows = enabledEngines => (enabledEngines || [])
  .map(id => ({ label: labelOfEngine(id), id, isSelected: true }));

// Full engine option list for the MultiselectField, with current selection applied.
export const engineMultiselectItems = enabledEngines => unionBy(
  [...selectedEngineRows(enabledEngines), ...AI_ENGINES.map(engine => ({ ...engine, isSelected: false }))],
  'id',
);

// Resolve the engine ids chosen after a MultiselectField close event.
export const enginesFromCloseEvent = (list, enabledEngines) => {
  const merged = unionBy([...list, ...selectedEngineRows(enabledEngines)], 'id');
  return merged.filter(item => item.isSelected || item.isSelected === undefined).map(item => item.id);
};

// Same set of engine ids regardless of order (used to skip no-op saves).
export const sameEngineSet = (a = [], b = []) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

// --- Provider / model selection (group-wide; mailbox #7) -------------------
// ai-assist is the source of truth for the catalog (fetched via /ai-assist/models
// as `{ provider, label, model, tier }`). These are only the display labels +
// the Product-approved default pair, used until the fetch lands / as a fallback.
export const AI_PROVIDER_LABELS = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
};

export const labelOfProvider = id => AI_PROVIDER_LABELS[id] || id;

// The default pick is owned by ai-assist's registry, surfaced as a `default: true`
// flag on one /ai-assist/models row. Used to show a selection for a group that has
// no explicit pick yet — NOT stored until the operator Applies. So an un-picked
// group always tracks the live registry default; an explicit pick is sticky.
export const defaultPick = (models = []) => {
  const found = models.find(model => model.default) || models[0];
  return found ? { provider: found.provider, model: found.model } : undefined;
};

// Distinct providers present in the fetched catalog, as SelectField rows.
export const companyOptions = (models = []) => {
  const seen = [];
  models.forEach(({ provider }) => {
    if (provider && !seen.includes(provider)) {
      seen.push(provider);
    }
  });
  return seen.map(id => ({ id, name: labelOfProvider(id) }));
};

// Models offered for one provider, as SelectField rows ({ id: model, name: label }).
export const modelOptions = (models, provider) => (models || [])
  .filter(model => model.provider === provider)
  .map(({ model, label }) => ({ id: model, name: label || model }));

// Keep a stored-but-no-longer-offered model visible as the current selection,
// rather than letting the dropdown blank/reset it (mailbox #8: offer:false stays
// runnable). Prepends a synthetic row when the current model isn't in the list.
export const withCurrentModel = (options, currentModel) => {
  const list = options || [];
  if (!currentModel || list.some(option => option.id === currentModel)) {
    return list;
  }
  return [{ id: currentModel, name: currentModel }, ...list];
};
